/**
 * MemoForm's submit orchestration: dirtiness/validity checks, the single
 * persist path (`submitNow`) shared by the modal Save button, ⌘/Ctrl+Enter,
 * and the inline autosave, and the form-level submit/keydown handlers that
 * gate it.
 *
 * Extracted from MemoForm so the component reads as state + layout, and so
 * the submit guards — emptiness, the untouched-edit no-op, inline
 * commit-and-exit — live together where they can't drift between the three
 * save paths that funnel through `submitNow`.
 */

import type { FormEvent, KeyboardEvent, RefObject } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { TimeValue } from 'react-aria-components';

import { combineDue, isContentEmpty, isMemoEditDirty } from './memoFormValues';
import type { MemoEntityType, MemoPrivacy } from './notes.utils';
import { useInlineMemoAutosave } from './useInlineMemoAutosave';
import type { Note } from './useMemoRecords';

export interface UseMemoFormSubmitOptions {
  mode: 'create' | 'edit';
  note: Note | undefined;
  entityType: MemoEntityType;
  title: string;
  content: string;
  date: string;
  time: TimeValue | null;
  ownerEntityId: string;
  privacy: MemoPrivacy;
  stagePickerOverride: string | null | undefined;
  /** Pending picker override (tasks) merged over the derived saved stage. */
  effectiveStageAssociation: string | null;
  onSubmit: (
    title: string,
    content: string,
    date?: string,
    ownerEntityId?: string,
    stageAssociation?: string | null,
    privacy?: MemoPrivacy
  ) => void;
  onCancel: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  isInline: boolean;
  isSubmitting: boolean;
  /** The form's own container — see useInlineMemoAutosave. */
  formRef: RefObject<HTMLFormElement | null>;
}

export interface UseMemoFormSubmitResult {
  /** A memo needs SOME substance: at least a title or content. */
  isValid: boolean;
  /** Edit mode only — has the user actually changed anything? Always `true` outside edit mode. */
  isEditDirty: boolean;
  handleSubmit: (e: FormEvent) => void;
  handleKeyDown: (e: KeyboardEvent) => void;
}

export function useMemoFormSubmit({
  mode,
  note,
  entityType,
  title,
  content,
  date,
  time,
  ownerEntityId,
  privacy,
  stagePickerOverride,
  effectiveStageAssociation,
  onSubmit,
  onCancel,
  onDirtyChange,
  isInline,
  isSubmitting,
  formRef,
}: UseMemoFormSubmitOptions): UseMemoFormSubmitResult {
  const isEditDirty = useMemo(
    () =>
      isMemoEditDirty({
        mode,
        note,
        entityType,
        title,
        content,
        date,
        time,
        ownerEntityId,
        privacy,
        stagePickerOverride,
      }),
    [
      mode,
      note,
      entityType,
      title,
      content,
      date,
      time,
      ownerEntityId,
      privacy,
      stagePickerOverride,
    ]
  );

  // Notify the parent when the form becomes dirty — the compose panels use this
  // to decide whether closing may discard edits.
  // - Create mode: dirty once the user has typed a title or body. `isEditDirty`
  //   returns true unconditionally for create/note-less (see isMemoEditDirty),
  //   so it can't drive the create guard — an untouched new form would falsely
  //   prompt "discard?". Use the tag-stripping emptiness check (not
  //   `content.trim()`) so the editor's on-mount `<p></p>` emit doesn't flag an
  //   untouched note as edited.
  // - Edit mode: use the full `isEditDirty` so a change to the due, stage,
  //   owner, or privacy (not just title/body) also guards against silently
  //   discarding edits on close.
  useEffect(() => {
    if (!onDirtyChange) {
      return;
    }
    if (mode === 'create') {
      onDirtyChange(title.trim().length > 0 || !isContentEmpty(content));
    } else {
      onDirtyChange(isEditDirty);
    }
  }, [mode, title, content, isEditDirty, onDirtyChange]);

  // The single persist call. Every save path — the modal's Save button, ⌘
  // Enter, and the inline autosave — funnels through here, so the argument
  // list (and the normalizations below) can't drift between them. Callers own
  // the guards: emptiness, and the untouched-edit no-op.
  const submitNow = useCallback(() => {
    onSubmit(
      title.trim(),
      // Normalize an empty body to `''` so a title-only note doesn't persist
      // the editor's placeholder `<p></p>` as its content.
      isContentEmpty(content) ? '' : content.trim(),
      combineDue(date, time),
      ownerEntityId || undefined,
      // Null when the user explicitly cleared the status (via "clear status"),
      // so callers can emit a remove-stage event on edit.
      effectiveStageAssociation,
      privacy
    );
  }, [
    onSubmit,
    title,
    content,
    date,
    time,
    ownerEntityId,
    effectiveStageAssociation,
    privacy,
  ]);

  // A memo needs SOME substance: at least a title or content. Both empty is
  // the only invalid state (mirrors the disabled submit button). A title-only
  // note is valid — NoteCard hides the title row when empty, and content can
  // be added later.
  const isValid = !isContentEmpty(content) || title.trim().length > 0;

  // ---- Inline autosave (record-slideout variant) -------------------------
  // The inline editor has no Save/Cancel: edits persist automatically and exit
  // returns to the read view. `saveInline` is the single persist path, reusing
  // the exact `onSubmit` the modal's Save button calls.
  const saveInline = useCallback(() => {
    // Empty note (nothing to save) or an untouched edit → no-op, so autosave
    // never emits a spurious event and exit is a clean close.
    if (!isValid) {
      return;
    }
    if (mode === 'edit' && !isEditDirty) {
      return;
    }
    submitNow();
  }, [isValid, mode, isEditDirty, submitNow]);

  // Debounced autosave, unmount-flush, and Esc/click-away-to-exit — see
  // useInlineMemoAutosave for the mechanics. `isEditDirty` already resolves
  // to `true` outside edit mode, so it doubles as this hook's dirty guard.
  const { commitAndExit } = useInlineMemoAutosave({
    enabled: isInline,
    isDirty: isEditDirty,
    commit: saveInline,
    onExit: onCancel,
    containerRef: formRef,
  });

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!isValid) {
        return;
      }
      // Saving an untouched note is a no-op on the backend that leaves the
      // editor stuck open — treat it as Cancel so the form closes cleanly.
      if (mode === 'edit' && !isEditDirty) {
        onCancel();
        return;
      }
      submitNow();
    },
    [isValid, mode, isEditDirty, onCancel, submitNow]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (isSubmitting) {
          return;
        }
        // Inline: ⌘/Ctrl+Enter commits and closes (there is no Save button).
        if (isInline) {
          commitAndExit();
          return;
        }
        if (!isValid) {
          return;
        }
        // Untouched edit → close instead of a no-op save (see handleSubmit).
        if (mode === 'edit' && !isEditDirty) {
          onCancel();
          return;
        }
        submitNow();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Inline: Esc commits and closes (no discard). Elsewhere it cancels.
        if (isInline) {
          commitAndExit();
          return;
        }
        onCancel();
      }
    },
    [
      isValid,
      isSubmitting,
      isInline,
      commitAndExit,
      mode,
      isEditDirty,
      onCancel,
      submitNow,
    ]
  );

  return { isValid, isEditDirty, handleSubmit, handleKeyDown };
}
